import React, { useEffect, useMemo, useState, Component } from 'react';
import { Search, Plus, Minus, X, Check, ShoppingCart, Trash2, Copy, ChevronDown, Globe, Loader2, FileSpreadsheet } from 'lucide-react';
import { recipes } from '../data/recipes';
import { motion, AnimatePresence } from 'framer-motion';
interface ShoppingIngredient {
  id: string;
  name: string;
  baseQuantity: number;
  unit: string;
  category: string;
  checked: boolean;
  isCustom?: boolean;
}
// Parse a quantity string like "200g" or "2 medium" into { amount, unit }
function parseQuantity(ingredient: string): {
  amount: number;
  unit: string;
} {
  // Common ingredient quantity defaults
  const defaults: Record<string, {
    amount: number;
    unit: string;
  }> = {
    'oreo cookies': {
      amount: 6,
      unit: 'pcs'
    },
    milk: {
      amount: 250,
      unit: 'ml'
    },
    'ice cream': {
      amount: 2,
      unit: 'scoops'
    },
    'whipped cream': {
      amount: 50,
      unit: 'ml'
    },
    'pav buns': {
      amount: 4,
      unit: 'pcs'
    },
    bhaji: {
      amount: 200,
      unit: 'g'
    },
    cheese: {
      amount: 100,
      unit: 'g'
    },
    butter: {
      amount: 50,
      unit: 'g'
    },
    'pizza sauce': {
      amount: 3,
      unit: 'tbsp'
    },
    tortilla: {
      amount: 4,
      unit: 'pcs'
    },
    paneer: {
      amount: 200,
      unit: 'g'
    },
    veggies: {
      amount: 200,
      unit: 'g'
    },
    vegetables: {
      amount: 200,
      unit: 'g'
    },
    'mixed vegetables': {
      amount: 200,
      unit: 'g'
    },
    chutney: {
      amount: 3,
      unit: 'tbsp'
    },
    rice: {
      amount: 200,
      unit: 'g'
    },
    rajma: {
      amount: 150,
      unit: 'g'
    },
    corn: {
      amount: 100,
      unit: 'g'
    },
    'soy sauce': {
      amount: 2,
      unit: 'tbsp'
    },
    sugar: {
      amount: 100,
      unit: 'g'
    },
    caramel: {
      amount: 50,
      unit: 'ml'
    },
    cardamom: {
      amount: 3,
      unit: 'pcs'
    },
    sooji: {
      amount: 200,
      unit: 'g'
    },
    curd: {
      amount: 100,
      unit: 'g'
    },
    besan: {
      amount: 150,
      unit: 'g'
    },
    eno: {
      amount: 1,
      unit: 'tsp'
    },
    'tandoori masala': {
      amount: 2,
      unit: 'tsp'
    },
    'puri shells': {
      amount: 20,
      unit: 'pcs'
    },
    spices: {
      amount: 2,
      unit: 'tsp'
    },
    sattu: {
      amount: 100,
      unit: 'g'
    },
    dates: {
      amount: 8,
      unit: 'pcs'
    },
    nuts: {
      amount: 50,
      unit: 'g'
    },
    coconut: {
      amount: 30,
      unit: 'g'
    },
    'bread slices': {
      amount: 4,
      unit: 'pcs'
    },
    khakhra: {
      amount: 4,
      unit: 'pcs'
    },
    'green chutney': {
      amount: 3,
      unit: 'tbsp'
    },
    tofu: {
      amount: 200,
      unit: 'g'
    },
    breadcrumbs: {
      amount: 50,
      unit: 'g'
    },
    cornflour: {
      amount: 2,
      unit: 'tbsp'
    },
    avocado: {
      amount: 1,
      unit: 'pc'
    },
    bread: {
      amount: 4,
      unit: 'slices'
    },
    lime: {
      amount: 1,
      unit: 'pc'
    },
    'soya chunks': {
      amount: 100,
      unit: 'g'
    },
    'chili sauce': {
      amount: 2,
      unit: 'tbsp'
    },
    capsicum: {
      amount: 2,
      unit: 'pcs'
    },
    pav: {
      amount: 4,
      unit: 'pcs'
    },
    masala: {
      amount: 2,
      unit: 'tsp'
    },
    'moong dal': {
      amount: 100,
      unit: 'g'
    },
    chickpeas: {
      amount: 150,
      unit: 'g'
    },
    chutneys: {
      amount: 4,
      unit: 'tbsp'
    },
    makhana: {
      amount: 100,
      unit: 'g'
    },
    ghee: {
      amount: 2,
      unit: 'tbsp'
    },
    cashews: {
      amount: 100,
      unit: 'g'
    },
    water: {
      amount: 200,
      unit: 'ml'
    },
    'full fat milk': {
      amount: 1,
      unit: 'litre'
    },
    'lemon juice or vinegar': {
      amount: 3,
      unit: 'tbsp'
    },
    'mustard seeds': {
      amount: 1,
      unit: 'tsp'
    },
    'curry leaves': {
      amount: 8,
      unit: 'pcs'
    },
    oil: {
      amount: 3,
      unit: 'tbsp'
    },
    'potato filling': {
      amount: 200,
      unit: 'g'
    },
    chocolate: {
      amount: 100,
      unit: 'g'
    },
    'condensed milk': {
      amount: 200,
      unit: 'ml'
    },
    walnuts: {
      amount: 50,
      unit: 'g'
    },
    'wheat flour': {
      amount: 200,
      unit: 'g'
    },
    potato: {
      amount: 2,
      unit: 'pcs'
    },
    oats: {
      amount: 100,
      unit: 'g'
    },
    spinach: {
      amount: 200,
      unit: 'g'
    },
    onion: {
      amount: 2,
      unit: 'pcs'
    },
    tomato: {
      amount: 2,
      unit: 'pcs'
    },
    turmeric: {
      amount: 1,
      unit: 'tsp'
    },
    yogurt: {
      amount: 100,
      unit: 'g'
    },
    cream: {
      amount: 50,
      unit: 'ml'
    },
    'kadhai masala': {
      amount: 2,
      unit: 'tsp'
    },
    'black pepper': {
      amount: 1,
      unit: 'tsp'
    },
    garlic: {
      amount: 4,
      unit: 'cloves'
    },
    'leftover roti': {
      amount: 4,
      unit: 'pcs'
    },
    roti: {
      amount: 4,
      unit: 'pcs'
    },
    salsa: {
      amount: 3,
      unit: 'tbsp'
    },
    'sour cream': {
      amount: 2,
      unit: 'tbsp'
    },
    tamarind: {
      amount: 2,
      unit: 'tbsp'
    },
    jaggery: {
      amount: 30,
      unit: 'g'
    },
    'black beans': {
      amount: 100,
      unit: 'g'
    },
    'basmati rice': {
      amount: 200,
      unit: 'g'
    },
    'dry fruits': {
      amount: 50,
      unit: 'g'
    },
    saffron: {
      amount: 1,
      unit: 'pinch'
    },
    'whole spices': {
      amount: 1,
      unit: 'tsp'
    },
    'chaat masala': {
      amount: 1,
      unit: 'tsp'
    },
    lemon: {
      amount: 1,
      unit: 'pc'
    },
    macaroni: {
      amount: 100,
      unit: 'g'
    },
    chapati: {
      amount: 4,
      unit: 'pcs'
    }
  };
  const key = ingredient.toLowerCase();
  if (defaults[key]) return defaults[key];
  return {
    amount: 100,
    unit: 'g'
  };
}
function categorizeIngredient(name: string): string {
  const lower = name.toLowerCase();
  const categories: Record<string, string[]> = {
    'Dairy & Protein': ['paneer', 'cheese', 'milk', 'cream', 'curd', 'yogurt', 'butter', 'ghee', 'tofu', 'sour cream', 'condensed milk', 'ice cream', 'whipped cream', 'full fat milk', 'feta'],
    'Grains & Flour': ['rice', 'wheat flour', 'sooji', 'besan', 'oats', 'bread', 'tortilla', 'roti', 'pav', 'macaroni', 'pasta', 'khakhra', 'chapati', 'breadcrumbs', 'cornflour', 'basmati rice'],
    Vegetables: ['onion', 'tomato', 'capsicum', 'potato', 'spinach', 'vegetables', 'veggies', 'mixed vegetables', 'corn', 'avocado', 'mushroom', 'cucumber'],
    'Spices & Seasonings': ['spices', 'masala', 'turmeric', 'cardamom', 'cumin', 'chaat masala', 'tandoori masala', 'kadhai masala', 'black pepper', 'mustard seeds', 'eno', 'saffron', 'whole spices', 'garlic', 'curry leaves'],
    'Sauces & Condiments': ['soy sauce', 'chili sauce', 'pizza sauce', 'chutney', 'green chutney', 'chutneys', 'salsa', 'tamarind', 'lemon juice or vinegar', 'oil', 'lime', 'lemon'],
    'Dry Fruits & Nuts': ['cashews', 'nuts', 'walnuts', 'dry fruits', 'dates', 'coconut', 'makhana'],
    'Legumes & Beans': ['rajma', 'chickpeas', 'moong dal', 'soya chunks', 'black beans', 'sattu', 'lentils'],
    'Sweets & Others': ['sugar', 'chocolate', 'jaggery', 'caramel', 'oreo cookies', 'water', 'puri shells']
  };
  for (const [cat, keywords] of Object.entries(categories)) {
    if (keywords.some(k => lower.includes(k) || k.includes(lower))) return cat;
  }
  return 'Other';
}
function parseOnlineMeasure(measure: string): {
  amount: number;
  unit: string;
  raw?: string;
} {
  if (!measure || !measure.trim()) return {
    amount: 1,
    unit: 'pc'
  };
  const cleaned = measure.trim().toLowerCase();
  // Non-numeric / descriptive measures — keep as-is so they don't scale wrongly
  const descriptiveTerms = ['to taste', 'as needed', 'a pinch', 'pinch', 'handful', 'splash', 'dash', 'few', 'some', 'for garnish', 'for serving', 'optional'];
  if (descriptiveTerms.some(t => cleaned === t || cleaned.includes(t))) {
    return {
      amount: 1,
      unit: cleaned,
      raw: measure.trim()
    };
  }
  // Try to extract number and unit
  const match = cleaned.match(/^([\d./]+)\s*(.*)$/);
  if (match) {
    let num = 0;
    if (match[1].includes('/')) {
      const parts = match[1].split('/');
      num = parseFloat(parts[0]) / parseFloat(parts[1]);
    } else {
      num = parseFloat(match[1]);
    }
    if (isNaN(num)) num = 1;
    let unit = match[2].trim() || 'pc';
    if (unit.startsWith('tbsp') || unit.startsWith('tablespoon')) unit = 'tbsp';else if (unit.startsWith('tsp') || unit.startsWith('teaspoon')) unit = 'tsp';else if (unit === 'g' || unit.startsWith('gram')) unit = 'g';else if (unit === 'ml' || unit.startsWith('millil')) unit = 'ml';else if (unit.startsWith('cup')) unit = 'cups';else if (unit.startsWith('oz') || unit.startsWith('ounce')) unit = 'oz';else if (unit.startsWith('lb') || unit.startsWith('pound')) unit = 'lb';else if (unit === 'kg' || unit.startsWith('kilo')) unit = 'kg';else if (unit === 'l' || unit === 'litre' || unit === 'liter') unit = 'litre';else if (!unit) unit = 'pc';
    return {
      amount: num,
      unit,
      raw: measure.trim()
    };
  }
  // No number — treat as descriptive
  return {
    amount: 1,
    unit: cleaned || 'pc',
    raw: measure.trim()
  };
}
function extractMealIngredients(meal: any): {
  name: string;
  measure: string;
}[] {
  const ingredients: {
    name: string;
    measure: string;
  }[] = [];
  for (let i = 1; i <= 20; i++) {
    const name = meal[`strIngredient${i}`];
    const measure = meal[`strMeasure${i}`];
    if (name && name.trim()) {
      ingredients.push({
        name: name.trim(),
        measure: measure?.trim() || ''
      });
    }
  }
  return ingredients;
}
// Simple CSV parser that handles quoted fields and commas inside quotes
function parseCSV(text: string): string[][] {
  const rows: string[][] = [];
  let current: string[] = [];
  let field = '';
  let inQuotes = false;
  for (let i = 0; i < text.length; i++) {
    const ch = text[i];
    if (inQuotes) {
      if (ch === '"' && text[i + 1] === '"') {
        field += '"';
        i++;
      } else if (ch === '"') {
        inQuotes = false;
      } else {
        field += ch;
      }
    } else {
      if (ch === '"') {
        inQuotes = true;
      } else if (ch === ',') {
        current.push(field);
        field = '';
      } else if (ch === '\n' || ch === '\r') {
        if (ch === '\r' && text[i + 1] === '\n') i++;
        current.push(field);
        if (current.some(c => c.trim() !== '')) rows.push(current);
        current = [];
        field = '';
      } else {
        field += ch;
      }
    }
  }
  if (field || current.length) {
    current.push(field);
    if (current.some(c => c.trim() !== '')) rows.push(current);
  }
  return rows;
}
// Parse Google Sheet CSV into SheetRecipe[]. Tries to be flexible about column layout.
function parseSheetToRecipes(csvText: string): SheetRecipe[] {
  const rows = parseCSV(csvText);
  if (rows.length === 0) return [];
  // Detect header row
  const header = rows[0].map(h => h.trim().toLowerCase());
  const hasHeader = header.some(h => /recipe|dish|name|ingredient|quantity|qty|amount|unit|measure/.test(h));
  const dataRows = hasHeader ? rows.slice(1) : rows;
  // Find column indexes (case-insensitive)
  const findCol = (patterns: RegExp[]) => {
    if (!hasHeader) return -1;
    for (let i = 0; i < header.length; i++) {
      if (patterns.some(p => p.test(header[i]))) return i;
    }
    return -1;
  };
  const recipeCol = hasHeader ? findCol([/recipe/, /dish/, /^name/]) : 0;
  const ingredientCol = findCol([/ingredient/, /item/]);
  const quantityCol = findCol([/quantity|qty|amount/]);
  const unitCol = findCol([/unit|measure/]);
  // Group by recipe name
  const map = new Map<string, {
    name: string;
    ingredients: {
      name: string;
      measure: string;
    }[];
  }>();
  for (const row of dataRows) {
    const recipeName = (row[recipeCol >= 0 ? recipeCol : 0] || '').trim();
    if (!recipeName) continue;
    const key = recipeName.toLowerCase();
    if (!map.has(key)) {
      map.set(key, {
        name: recipeName,
        ingredients: []
      });
    }
    const entry = map.get(key)!;
    // Long format: one ingredient per row
    if (ingredientCol >= 0) {
      const ingName = (row[ingredientCol] || '').trim();
      if (!ingName) continue;
      let measure = '';
      const qty = quantityCol >= 0 ? (row[quantityCol] || '').trim() : '';
      const unit = unitCol >= 0 ? (row[unitCol] || '').trim() : '';
      if (qty && unit) measure = `${qty} ${unit}`;else if (qty) measure = qty;else if (unit) measure = unit;
      entry.ingredients.push({
        name: ingName,
        measure
      });
    } else {
      // Wide format: ingredients spread across remaining columns
      for (let i = 0; i < row.length; i++) {
        if (i === (recipeCol >= 0 ? recipeCol : 0)) continue;
        const cell = (row[i] || '').trim();
        if (!cell) continue;
        // Try to split "ingredient - qty" or "ingredient: qty" or "ingredient (qty)"
        const splitMatch = cell.match(/^(.+?)\s*[-:–—(]\s*(.+?)\)?$/);
        if (splitMatch) {
          entry.ingredients.push({
            name: splitMatch[1].trim(),
            measure: splitMatch[2].trim()
          });
        } else {
          entry.ingredients.push({
            name: cell,
            measure: ''
          });
        }
      }
    }
  }
  return Array.from(map.entries()).map(([key, v], idx) => ({
    id: `sheet-${idx}-${key}`,
    name: v.name,
    ingredients: v.ingredients
  }));
}
// Normalize a string for fuzzy comparison: lowercase, strip accents/punctuation/extra spaces
function normalize(s: string): string {
  return s.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/[^a-z0-9\s]/g, ' ').replace(/\s+/g, ' ').trim();
}
// Check if all chars of `query` appear in `target` in order (subsequence match)
function isSubsequence(query: string, target: string): boolean {
  let i = 0;
  for (let j = 0; j < target.length && i < query.length; j++) {
    if (target[j] === query[i]) i++;
  }
  return i === query.length;
}
// Levenshtein distance
function levenshtein(a: string, b: string): number {
  if (a === b) return 0;
  if (!a.length) return b.length;
  if (!b.length) return a.length;
  const prev: number[] = new Array(b.length + 1);
  for (let j = 0; j <= b.length; j++) prev[j] = j;
  for (let i = 1; i <= a.length; i++) {
    const curr: number[] = [i];
    for (let j = 1; j <= b.length; j++) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1;
      curr[j] = Math.min(curr[j - 1] + 1, prev[j] + 1, prev[j - 1] + cost);
    }
    for (let j = 0; j <= b.length; j++) prev[j] = curr[j];
  }
  return prev[b.length];
}
// Score a single query token against a single target token. Returns 0-100.
function scoreToken(qTok: string, tTok: string): number {
  if (!qTok || !tTok) return 0;
  if (qTok === tTok) return 100;
  if (tTok.startsWith(qTok)) return 90;
  if (tTok.includes(qTok)) return 80;
  if (qTok.length >= 3 && isSubsequence(qTok, tTok)) {
    // tighter score if lengths are close
    const ratio = qTok.length / tTok.length;
    return Math.round(60 * ratio + 20);
  }
  // Edit distance — allow ~35% typos
  const threshold = Math.max(1, Math.floor(qTok.length * 0.35));
  if (Math.abs(qTok.length - tTok.length) <= threshold + 1) {
    const dist = levenshtein(qTok, tTok);
    if (dist <= threshold) {
      return Math.max(40, 80 - dist * 15);
    }
  }
  return 0;
}
// Score a query against a full target string. Splits query into tokens —
// EVERY query token must match somewhere in the target. Returns 0 if any token
// fails to find a match. Otherwise returns the sum of token scores.
function fuzzyScore(query: string, target: string): number {
  const q = normalize(query);
  const t = normalize(target);
  if (!q || !t) return 0;
  // Whole-string fast paths
  if (t === q) return 1000;
  if (t.includes(q)) return 500 + q.length * 5;
  const qTokens = q.split(' ').filter(x => x.length > 0);
  const tTokens = t.split(' ').filter(x => x.length > 0);
  if (qTokens.length === 0 || tTokens.length === 0) return 0;
  let total = 0;
  for (const qTok of qTokens) {
    let best = 0;
    for (const tTok of tTokens) {
      const s = scoreToken(qTok, tTok);
      if (s > best) best = s;
    }
    // Also try matching the whole target as one chunk (covers "panrmasala" → "paneer masala")
    if (best === 0) {
      const wholeScore = scoreToken(qTok, t.replace(/\s+/g, ''));
      if (wholeScore > best) best = Math.floor(wholeScore * 0.7);
    }
    if (best === 0) return 0; // every token must match
    total += best;
  }
  return total;
}
function fuzzyMatch(query: string, target: string): boolean {
  return fuzzyScore(query, target) > 0;
}
const SHEET_ID = '1bXAPTCV1KqUxYKDQYZQ4Ek1KXf5VCdBZyGjVitwl5_Q';
const SHEET_GID = '0';
const SHEET_CSV_URL = `https://docs.google.com/spreadsheets/d/${SHEET_ID}/export?format=csv&gid=${SHEET_GID}`;
interface OnlineMeal {
  idMeal: string;
  strMeal: string;
  strCategory: string;
  strArea: string;
  strMealThumb: string;
  ingredients: {
    name: string;
    measure: string;
  }[];
}
interface SheetRecipe {
  id: string;
  name: string;
  ingredients: {
    name: string;
    measure: string;
  }[];
}
export function ShoppingList() {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedRecipeIds, setSelectedRecipeIds] = useState<number[]>([]);
  const [servings, setServings] = useState(2);
  const [baseServings] = useState(2);
  const [ingredients, setIngredients] = useState<ShoppingIngredient[]>([]);
  const [customItemInput, setCustomItemInput] = useState('');
  const [showSearch, setShowSearch] = useState(true);
  const [copiedToClipboard, setCopiedToClipboard] = useState(false);
  // Online recipe states
  const [onlineMeals, setOnlineMeals] = useState<OnlineMeal[]>([]);
  const [onlineLoading, setOnlineLoading] = useState(false);
  const [onlineError, setOnlineError] = useState('');
  const [onlineSearched, setOnlineSearched] = useState(false);
  const [addedOnlineMeals, setAddedOnlineMeals] = useState<string[]>([]);
  // Google Sheet states
  const [sheetData, setSheetData] = useState<SheetRecipe[] | null>(null);
  const [sheetLoading, setSheetLoading] = useState(false);
  const [sheetError, setSheetError] = useState('');
  const [sheetSearched, setSheetSearched] = useState(false);
  const [sheetMatches, setSheetMatches] = useState<SheetRecipe[]>([]);
  const [addedSheetRecipes, setAddedSheetRecipes] = useState<string[]>([]);
  // Compute filtered + scored local recipes. Empty query = no list shown.
  const filteredRecipes = useMemo(() => {
    const q = searchQuery.trim();
    if (!q) return [];
    const scored = recipes.map(r => {
      const nameScore = fuzzyScore(q, r.name);
      const cuisineScore = fuzzyScore(q, r.cuisine) * 0.5;
      const ingScore = Math.max(0, ...r.ingredients.map(i => fuzzyScore(q, i) * 0.4));
      return {
        recipe: r,
        score: Math.max(nameScore, cuisineScore, ingScore)
      };
    }).filter(x => x.score > 0).sort((a, b) => b.score - a.score).slice(0, 12);
    return scored.map(x => x.recipe);
  }, [searchQuery]);
  // Fetch from TheMealDB when no local results
  const searchOnline = async () => {
    if (!searchQuery.trim()) return;
    setOnlineLoading(true);
    setOnlineError('');
    setOnlineSearched(true);
    try {
      const res = await fetch(`https://www.themealdb.com/api/json/v1/1/search.php?s=${encodeURIComponent(searchQuery.trim())}`);
      const data = await res.json();
      if (data.meals && data.meals.length > 0) {
        const meals: OnlineMeal[] = data.meals.slice(0, 6).map((meal: any) => ({
          idMeal: meal.idMeal,
          strMeal: meal.strMeal,
          strCategory: meal.strCategory || '',
          strArea: meal.strArea || '',
          strMealThumb: meal.strMealThumb || '',
          ingredients: extractMealIngredients(meal)
        }));
        setOnlineMeals(meals);
      } else {
        setOnlineMeals([]);
        // No web results — automatically try the sheet
        searchSheet();
      }
    } catch (err) {
      setOnlineError('Could not fetch recipes. Please try again.');
      setOnlineMeals([]);
    } finally {
      setOnlineLoading(false);
    }
  };
  // Fetch + search the Google Sheet (scored fuzzy)
  const searchSheet = async () => {
    if (!searchQuery.trim()) return;
    setSheetLoading(true);
    setSheetError('');
    setSheetSearched(true);
    try {
      let data = sheetData;
      if (!data) {
        const res = await fetch(SHEET_CSV_URL);
        if (!res.ok) {
          throw new Error('Sheet not accessible — make sure it is shared as "Anyone with the link can view"');
        }
        const text = await res.text();
        data = parseSheetToRecipes(text);
        setSheetData(data);
      }
      const scored = data.map(r => {
        const nameScore = fuzzyScore(searchQuery, r.name);
        const ingScore = Math.max(0, ...r.ingredients.map(i => fuzzyScore(searchQuery, i.name) * 0.4));
        return {
          recipe: r,
          score: Math.max(nameScore, ingScore)
        };
      }).filter(x => x.score > 0).sort((a, b) => b.score - a.score).slice(0, 8);
      setSheetMatches(scored.map(x => x.recipe));
    } catch (err: any) {
      setSheetError(err?.message || 'Could not load your recipe sheet. Make sure it is shared publicly.');
      setSheetMatches([]);
    } finally {
      setSheetLoading(false);
    }
  };
  // Reset all fallback results when search query changes
  useEffect(() => {
    setOnlineSearched(false);
    setOnlineMeals([]);
    setOnlineError('');
    setSheetSearched(false);
    setSheetMatches([]);
    setSheetError('');
  }, [searchQuery]);
  // Auto-trigger waterfall: web → sheet whenever user types 3+ chars (debounced)
  useEffect(() => {
    const q = searchQuery.trim();
    if (q.length < 3) return;
    const timer = setTimeout(() => {
      searchOnline();
    }, 600);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery]);
  const addOnlineMeal = (meal: OnlineMeal) => {
    if (addedOnlineMeals.includes(meal.idMeal)) return;
    setAddedOnlineMeals(prev => [...prev, meal.idMeal]);
    const newIngredients: ShoppingIngredient[] = meal.ingredients.map((ing, idx) => {
      const parsed = parseOnlineMeasure(ing.measure);
      const id = `online-${meal.idMeal}-${idx}`;
      return {
        id,
        name: ing.name.charAt(0).toUpperCase() + ing.name.slice(1),
        baseQuantity: parsed.amount,
        unit: parsed.unit,
        category: categorizeIngredient(ing.name),
        checked: false
      };
    });
    setIngredients(prev => {
      const updated = [...prev];
      for (const newIng of newIngredients) {
        const existingIdx = updated.findIndex(i => i.name.toLowerCase() === newIng.name.toLowerCase());
        if (existingIdx >= 0) {
          updated[existingIdx] = {
            ...updated[existingIdx],
            baseQuantity: updated[existingIdx].baseQuantity + newIng.baseQuantity
          };
        } else {
          updated.push(newIng);
        }
      }
      return updated;
    });
    setSearchQuery('');
    setShowSearch(false);
  };
  const addSheetRecipe = (recipe: SheetRecipe) => {
    if (addedSheetRecipes.includes(recipe.id)) return;
    setAddedSheetRecipes(prev => [...prev, recipe.id]);
    const newIngredients: ShoppingIngredient[] = recipe.ingredients.map((ing, idx) => {
      const parsed = parseOnlineMeasure(ing.measure);
      return {
        id: `${recipe.id}-${idx}`,
        name: ing.name.charAt(0).toUpperCase() + ing.name.slice(1),
        baseQuantity: parsed.amount,
        unit: parsed.unit,
        category: categorizeIngredient(ing.name),
        checked: false
      };
    });
    setIngredients(prev => {
      const updated = [...prev];
      for (const newIng of newIngredients) {
        const existingIdx = updated.findIndex(i => i.name.toLowerCase() === newIng.name.toLowerCase());
        if (existingIdx >= 0) {
          updated[existingIdx] = {
            ...updated[existingIdx],
            baseQuantity: updated[existingIdx].baseQuantity + newIng.baseQuantity
          };
        } else {
          updated.push(newIng);
        }
      }
      return updated;
    });
    setSearchQuery('');
    setShowSearch(false);
  };
  const addRecipe = (recipeId: number) => {
    if (selectedRecipeIds.includes(recipeId)) return;
    const recipe = recipes.find(r => r.id === recipeId);
    if (!recipe) return;
    setSelectedRecipeIds(prev => [...prev, recipeId]);
    const newIngredients: ShoppingIngredient[] = recipe.ingredients.map((ing, idx) => {
      const parsed = parseQuantity(ing);
      const id = `${recipeId}-${idx}`;
      // Check if ingredient already exists
      const existing = ingredients.find(i => i.name.toLowerCase() === ing.toLowerCase() && !i.isCustom);
      if (existing) return null as unknown as ShoppingIngredient; // will filter out
      return {
        id,
        name: ing.charAt(0).toUpperCase() + ing.slice(1),
        baseQuantity: parsed.amount,
        unit: parsed.unit,
        category: categorizeIngredient(ing),
        checked: false
      };
    }).filter(Boolean);
    // Merge: increase quantity for duplicates, add new ones
    setIngredients(prev => {
      const updated = [...prev];
      for (const newIng of newIngredients) {
        const existingIdx = updated.findIndex(i => i.name.toLowerCase() === newIng.name.toLowerCase());
        if (existingIdx >= 0) {
          updated[existingIdx] = {
            ...updated[existingIdx],
            baseQuantity: updated[existingIdx].baseQuantity + newIng.baseQuantity
          };
        } else {
          updated.push(newIng);
        }
      }
      return updated;
    });
    setSearchQuery('');
    setShowSearch(false);
  };
  const removeRecipe = (recipeId: number) => {
    const recipe = recipes.find(r => r.id === recipeId);
    if (!recipe) return;
    setSelectedRecipeIds(prev => prev.filter(id => id !== recipeId));
    // Remove ingredients that came from this recipe (simplified: remove by name match)
    const recipeIngNames = new Set(recipe.ingredients.map(i => i.toLowerCase()));
    setIngredients(prev => prev.filter(i => i.isCustom || !recipeIngNames.has(i.name.toLowerCase())));
  };
  const toggleChecked = (id: string) => {
    setIngredients(prev => prev.map(i => i.id === id ? {
      ...i,
      checked: !i.checked
    } : i));
  };
  const addCustomItem = () => {
    const trimmed = customItemInput.trim();
    if (!trimmed) return;
    setIngredients(prev => [...prev, {
      id: `custom-${Date.now()}`,
      name: trimmed.charAt(0).toUpperCase() + trimmed.slice(1),
      baseQuantity: 1,
      unit: 'pc',
      category: categorizeIngredient(trimmed),
      checked: false,
      isCustom: true
    }]);
    setCustomItemInput('');
  };
  const removeItem = (id: string) => {
    setIngredients(prev => prev.filter(i => i.id !== id));
  };
  const clearAll = () => {
    setIngredients([]);
    setSelectedRecipeIds([]);
    setAddedOnlineMeals([]);
    setAddedSheetRecipes([]);
    setServings(2);
    setShowSearch(true);
  };
  const multiplier = servings / baseServings;
  const groupedIngredients = useMemo(() => {
    const groups: Record<string, ShoppingIngredient[]> = {};
    for (const ing of ingredients) {
      if (!groups[ing.category]) groups[ing.category] = [];
      groups[ing.category].push(ing);
    }
    return groups;
  }, [ingredients]);
  const checkedCount = ingredients.filter(i => i.checked).length;
  const totalCount = ingredients.length;
  const formatQuantity = (base: number, unit: string) => {
    // Descriptive units like "to taste", "a pinch", "handful" — don't scale
    const descriptiveKeywords = ['to taste', 'as needed', 'handful', 'splash', 'dash', 'few', 'some', 'garnish', 'serving', 'optional'];
    if (descriptiveKeywords.some(k => unit.includes(k))) {
      return unit;
    }
    const scaled = Math.round(base * multiplier * 10) / 10;
    if (unit === 'pcs' || unit === 'pc' || unit === 'cloves' || unit === 'scoops' || unit === 'slices') {
      return `${Math.ceil(scaled)} ${unit}`;
    }
    if (unit === 'pinch') return `${Math.max(1, Math.ceil(scaled))} ${unit}`;
    return `${scaled} ${unit}`;
  };
  const copyToClipboard = () => {
    const text = Object.entries(groupedIngredients).map(([cat, items]) => {
      const lines = items.map(i => `${i.checked ? '✅' : '⬜'} ${i.name} — ${formatQuantity(i.baseQuantity, i.unit)}`);
      return `${cat}:\n${lines.join('\n')}`;
    }).join('\n\n');
    const header = `🛒 Shopping List (${servings} servings)\nRecipes: ${selectedRecipeIds.map(id => recipes.find(r => r.id === id)?.name).filter(Boolean).join(', ')}\n\n`;
    navigator.clipboard.writeText(header + text);
    setCopiedToClipboard(true);
    setTimeout(() => setCopiedToClipboard(false), 2000);
  };
  return <section className="py-20 px-4 bg-gradient-to-br from-green-50 to-blue-50" data-id="element-340">
      <div className="max-w-4xl mx-auto" data-id="element-341">
        <div className="text-center mb-12" data-id="element-342">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-blue-100 text-blue-700 font-semibold text-sm mb-4" data-id="element-343">
            <ShoppingCart size={16} data-id="element-344" /> Smart Shopping List
          </div>
          <h2 className="text-3xl md:text-5xl font-bold text-stone-900 mb-4" style={{
          fontFamily: 'Poppins, sans-serif'
        }} data-id="element-345">
            🛒 Shopping List Builder
          </h2>
          <p className="text-stone-500 text-lg max-w-xl mx-auto" data-id="element-346">
            Select recipes, adjust portions, and get a dynamic ingredient list
            ready for your grocery run.
          </p>
        </div>

        {/* Recipe Search & Selection */}
        <div className="bg-white rounded-3xl shadow-xl border border-stone-100 p-6 md:p-8 mb-8" data-id="element-347">
          {/* Selected Recipes */}
          {selectedRecipeIds.length > 0 && <div className="mb-6" data-id="element-348">
              <label className="block text-sm font-bold text-stone-700 mb-3" data-id="element-349">
                📋 Selected Recipes
              </label>
              <div className="flex flex-wrap gap-2" data-id="element-350">
                {selectedRecipeIds.map(id => {
              const recipe = recipes.find(r => r.id === id);
              if (!recipe) return null;
              return <span key={id} className="flex items-center gap-1.5 bg-green-100 text-green-800 text-sm font-semibold px-3 py-1.5 rounded-full" data-id="element-351">
                      {recipe.name}
                      <button onClick={() => removeRecipe(id)} className="hover:text-red-500 transition-colors" data-id="element-352">
                        <X size={14} data-id="element-353" />
                      </button>
                    </span>;
            })}
                <button onClick={() => setShowSearch(true)} className="flex items-center gap-1 text-green-600 hover:text-green-700 text-sm font-semibold px-3 py-1.5 rounded-full border border-dashed border-green-300 hover:border-green-400 transition-colors" data-id="element-354">
                  <Plus size={14} data-id="element-355" /> Add More
                </button>
              </div>
            </div>}

          {/* Search */}
          {showSearch && <div className="mb-6" data-id="element-356">
              <label className="block text-sm font-bold text-stone-700 mb-2" data-id="element-357">
                🔍 Search & Add Recipes
              </label>
              <div className="relative" data-id="element-358">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-stone-400" size={18} data-id="element-359" />
                <input type="text" value={searchQuery} onChange={e => setSearchQuery(e.target.value)} placeholder="Search by name, ingredient, or cuisine..." className="w-full pl-11 pr-4 py-3 border border-stone-200 rounded-xl bg-stone-50 focus:border-green-400 focus:ring-2 focus:ring-green-100 outline-none text-sm" data-id="element-360" />
              </div>

              <div className="mt-3 max-h-[28rem] overflow-y-auto space-y-4" data-id="element-361">
                {/* === SECTION 1: Local (BeVegToday) results === */}
                {filteredRecipes.length > 0 && <div data-id="element-362">
                    {searchQuery.trim() && <div className="flex items-center gap-2 mb-2" data-id="element-363">
                        <span className="text-xs font-bold text-green-700 uppercase tracking-wider" data-id="element-364">
                          🎬 From BeVegToday Videos
                        </span>
                        <span className="text-xs text-stone-400" data-id="element-365">
                          ({filteredRecipes.length})
                        </span>
                      </div>}
                    <div className="space-y-1.5" data-id="element-366">
                      {filteredRecipes.map(recipe => {
                  const isSelected = selectedRecipeIds.includes(recipe.id);
                  return <button key={recipe.id} onClick={() => !isSelected && addRecipe(recipe.id)} disabled={isSelected} className={`w-full text-left flex items-center justify-between p-3 rounded-xl transition-all ${isSelected ? 'bg-green-50 border border-green-200 opacity-60 cursor-not-allowed' : 'bg-stone-50 hover:bg-green-50 border border-transparent hover:border-green-200'}`} data-id="element-367">
                            <div className="flex-1 min-w-0" data-id="element-368">
                              <p className="font-semibold text-stone-900 text-sm truncate" data-id="element-369">
                                {recipe.name}
                              </p>
                              <p className="text-xs text-stone-400 truncate" data-id="element-370">
                                {recipe.cuisine} · {recipe.ingredients.length}{' '}
                                ingredients · {recipe.cookTime} min
                              </p>
                            </div>
                            {isSelected ? <span className="text-green-600 text-xs font-bold flex items-center gap-1" data-id="element-371">
                                <Check size={14} data-id="element-372" /> Added
                              </span> : <span className="text-green-600 text-xs font-bold flex items-center gap-1" data-id="element-373">
                                <Plus size={14} data-id="element-374" /> Add
                              </span>}
                          </button>;
                })}
                    </div>
                  </div>}

                {/* === SECTION 2: Online (TheMealDB) results — shown whenever there's a query === */}
                {searchQuery.trim().length >= 3 && <div data-id="element-375">
                    {onlineLoading && <div className="flex items-center gap-2 py-2" data-id="element-376">
                        <Loader2 size={16} className="animate-spin text-blue-500" data-id="element-377" />
                        <span className="text-sm text-stone-500" data-id="element-378">
                          Searching the web...
                        </span>
                      </div>}

                    {onlineSearched && !onlineLoading && onlineMeals.length > 0 && <>
                          <div className="flex items-center gap-2 mb-2" data-id="element-379">
                            <Globe size={14} className="text-blue-500" data-id="element-380" />
                            <span className="text-xs font-bold text-blue-600 uppercase tracking-wider" data-id="element-381">
                              From the Web
                            </span>
                            <span className="text-xs text-stone-400" data-id="element-382">
                              ({onlineMeals.length})
                            </span>
                          </div>
                          <div className="space-y-1.5" data-id="element-383">
                            {onlineMeals.map(meal => {
                    const isAdded = addedOnlineMeals.includes(meal.idMeal);
                    return <button key={meal.idMeal} onClick={() => !isAdded && addOnlineMeal(meal)} disabled={isAdded} className={`w-full text-left flex items-center gap-3 p-3 rounded-xl transition-all ${isAdded ? 'bg-blue-50 border border-blue-200 opacity-60 cursor-not-allowed' : 'bg-stone-50 hover:bg-blue-50 border border-transparent hover:border-blue-200'}`} data-id="element-384">
                                  {meal.strMealThumb && <img src={meal.strMealThumb + '/preview'} alt={meal.strMeal} className="w-10 h-10 rounded-lg object-cover flex-shrink-0" data-id="element-385" />}
                                  <div className="flex-1 min-w-0" data-id="element-386">
                                    <p className="font-semibold text-stone-900 text-sm truncate" data-id="element-387">
                                      {meal.strMeal}
                                    </p>
                                    <p className="text-xs text-stone-400 truncate" data-id="element-388">
                                      {meal.strArea} · {meal.strCategory} ·{' '}
                                      {meal.ingredients.length} ingredients
                                    </p>
                                  </div>
                                  {isAdded ? <span className="text-blue-600 text-xs font-bold flex items-center gap-1 flex-shrink-0" data-id="element-389">
                                      <Check size={14} data-id="element-390" /> Added
                                    </span> : <span className="text-blue-600 text-xs font-bold flex items-center gap-1 flex-shrink-0" data-id="element-391">
                                      <Plus size={14} data-id="element-392" /> Add
                                    </span>}
                                </button>;
                  })}
                          </div>
                        </>}

                    {onlineError && <div className="flex items-center justify-between gap-2 py-2" data-id="element-393">
                        <span className="text-sm text-red-500" data-id="element-394">
                          {onlineError}
                        </span>
                        <button onClick={searchOnline} className="text-xs font-semibold text-blue-600 hover:text-blue-700" data-id="element-395">
                          Retry
                        </button>
                      </div>}
                  </div>}

                {/* === SECTION 3: Google Sheet results === */}
                {searchQuery.trim().length >= 3 && <div data-id="element-396">
                    {sheetLoading && <div className="flex items-center gap-2 py-2" data-id="element-397">
                        <Loader2 size={16} className="animate-spin text-purple-500" data-id="element-398" />
                        <span className="text-sm text-stone-500" data-id="element-399">
                          Searching your recipe sheet...
                        </span>
                      </div>}

                    {sheetSearched && !sheetLoading && sheetMatches.length > 0 && <>
                          <div className="flex items-center gap-2 mb-2" data-id="element-400">
                            <FileSpreadsheet size={14} className="text-purple-500" data-id="element-401" />
                            <span className="text-xs font-bold text-purple-600 uppercase tracking-wider" data-id="element-402">
                              From Your Recipe Sheet
                            </span>
                            <span className="text-xs text-stone-400" data-id="element-403">
                              ({sheetMatches.length})
                            </span>
                          </div>
                          <div className="space-y-1.5" data-id="element-404">
                            {sheetMatches.map(recipe => {
                    const isAdded = addedSheetRecipes.includes(recipe.id);
                    return <button key={recipe.id} onClick={() => !isAdded && addSheetRecipe(recipe)} disabled={isAdded} className={`w-full text-left flex items-center gap-3 p-3 rounded-xl transition-all ${isAdded ? 'bg-purple-50 border border-purple-200 opacity-60 cursor-not-allowed' : 'bg-stone-50 hover:bg-purple-50 border border-transparent hover:border-purple-200'}`} data-id="element-405">
                                  <div className="w-10 h-10 rounded-lg bg-purple-100 flex items-center justify-center flex-shrink-0" data-id="element-406">
                                    <FileSpreadsheet size={18} className="text-purple-600" data-id="element-407" />
                                  </div>
                                  <div className="flex-1 min-w-0" data-id="element-408">
                                    <p className="font-semibold text-stone-900 text-sm truncate" data-id="element-409">
                                      {recipe.name}
                                    </p>
                                    <p className="text-xs text-stone-400 truncate" data-id="element-410">
                                      Your sheet · {recipe.ingredients.length}{' '}
                                      ingredients · Base: 2 servings
                                    </p>
                                  </div>
                                  {isAdded ? <span className="text-purple-600 text-xs font-bold flex items-center gap-1 flex-shrink-0" data-id="element-411">
                                      <Check size={14} data-id="element-412" /> Added
                                    </span> : <span className="text-purple-600 text-xs font-bold flex items-center gap-1 flex-shrink-0" data-id="element-413">
                                      <Plus size={14} data-id="element-414" /> Add
                                    </span>}
                                </button>;
                  })}
                          </div>
                        </>}

                    {sheetError && <div className="mt-2 p-3 rounded-xl bg-amber-50 border border-amber-200" data-id="element-415">
                        <p className="text-sm text-amber-800 font-semibold mb-1 flex items-center gap-1.5" data-id="element-416">
                          <FileSpreadsheet size={14} data-id="element-417" /> Recipe sheet unavailable
                        </p>
                        <p className="text-xs text-amber-700" data-id="element-418">{sheetError}</p>
                      </div>}
                  </div>}

                {/* === Nothing found anywhere === */}
                {searchQuery.trim().length >= 3 && filteredRecipes.length === 0 && onlineSearched && !onlineLoading && onlineMeals.length === 0 && sheetSearched && !sheetLoading && sheetMatches.length === 0 && !sheetError && <p className="text-center text-stone-400 text-sm py-4" data-id="element-419">
                      No recipes found anywhere for "{searchQuery}". Try a
                      different search term.
                    </p>}

                {/* Hint for very short queries */}
                {searchQuery.trim().length > 0 && searchQuery.trim().length < 3 && <p className="text-center text-stone-400 text-xs py-2" data-id="element-420">
                      Type 3 or more characters to search the web and your sheet
                    </p>}
              </div>
            </div>}

          {/* Portion Size Adjuster */}
          {ingredients.length > 0 && <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 pt-4 border-t border-stone-100" data-id="element-421">
              <div data-id="element-422">
                <label className="block text-sm font-bold text-stone-700 mb-2" data-id="element-423">
                  🍽️ Adjust Portions
                </label>
                <div className="flex items-center gap-3" data-id="element-424">
                  <button onClick={() => setServings(Math.max(1, servings - 1))} className="w-10 h-10 rounded-full bg-stone-100 hover:bg-stone-200 flex items-center justify-center transition-colors" data-id="element-425">
                    <Minus size={18} data-id="element-426" />
                  </button>
                  <div className="text-center min-w-[80px]" data-id="element-427">
                    <p className="text-3xl font-bold text-green-600" style={{
                  fontFamily: 'Poppins, sans-serif'
                }} data-id="element-428">
                      {servings}
                    </p>
                    <p className="text-xs text-stone-400" data-id="element-429">servings</p>
                  </div>
                  <button onClick={() => setServings(Math.min(20, servings + 1))} className="w-10 h-10 rounded-full bg-stone-100 hover:bg-stone-200 flex items-center justify-center transition-colors" data-id="element-430">
                    <Plus size={18} data-id="element-431" />
                  </button>
                </div>
                <p className="text-xs text-stone-400 mt-2" data-id="element-432">
                  Base quantities are for 2 servings — totals scale
                  automatically.
                </p>
              </div>

              <div className="flex gap-2" data-id="element-433">
                <button onClick={copyToClipboard} className="flex items-center gap-2 px-4 py-2 bg-blue-100 hover:bg-blue-200 text-blue-700 rounded-xl text-sm font-semibold transition-colors" data-id="element-434">
                  {copiedToClipboard ? <>
                      <Check size={14} data-id="element-435" /> Copied!
                    </> : <>
                      <Copy size={14} data-id="element-436" /> Copy List
                    </>}
                </button>
                <button onClick={clearAll} className="flex items-center gap-2 px-4 py-2 bg-red-50 hover:bg-red-100 text-red-600 rounded-xl text-sm font-semibold transition-colors" data-id="element-437">
                  <Trash2 size={14} data-id="element-438" /> Clear All
                </button>
              </div>
            </div>}
        </div>

        {/* Shopping List */}
        <AnimatePresence data-id="element-439">
          {ingredients.length > 0 && <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0
        }} className="space-y-6" data-id="element-440">
              {/* Progress */}
              <div className="bg-white rounded-2xl p-4 shadow-md border border-stone-100" data-id="element-441">
                <div className="flex items-center justify-between mb-2" data-id="element-442">
                  <p className="text-sm font-bold text-stone-700" data-id="element-443">
                    Shopping Progress
                  </p>
                  <p className="text-sm text-stone-500" data-id="element-444">
                    <span className="font-bold text-green-600" data-id="element-445">
                      {checkedCount}
                    </span>{' '}
                    / {totalCount} items
                  </p>
                </div>
                <div className="w-full h-3 bg-stone-100 rounded-full overflow-hidden" data-id="element-446">
                  <motion.div className="h-full bg-gradient-to-r from-green-500 to-green-400 rounded-full" initial={{
                width: 0
              }} animate={{
                width: totalCount > 0 ? `${checkedCount / totalCount * 100}%` : '0%'
              }} transition={{
                duration: 0.4
              }} data-id="element-447" />
                </div>
              </div>

              {/* Grouped Ingredients */}
              {Object.entries(groupedIngredients).map(([category, items]) => <div key={category} className="bg-white rounded-2xl shadow-md border border-stone-100 overflow-hidden" data-id="element-448">
                  <div className="px-6 py-4 bg-stone-50 border-b border-stone-100" data-id="element-449">
                    <h3 className="font-bold text-stone-800 text-sm uppercase tracking-wider" data-id="element-450">
                      {category}
                    </h3>
                  </div>
                  <ul className="divide-y divide-stone-50" data-id="element-451">
                    {items.map((item, idx) => <motion.li key={item.id} initial={{
                opacity: 0,
                x: -10
              }} animate={{
                opacity: 1,
                x: 0
              }} transition={{
                delay: idx * 0.03
              }} className={`flex items-center gap-4 px-6 py-3.5 transition-colors ${item.checked ? 'bg-green-50/50' : ''}`} data-id="element-452">
                        <button onClick={() => toggleChecked(item.id)} className={`flex-shrink-0 w-6 h-6 rounded-lg border-2 flex items-center justify-center transition-all ${item.checked ? 'bg-green-500 border-green-500 text-white' : 'border-stone-300 hover:border-green-400'}`} data-id="element-453">
                          {item.checked && <Check size={14} data-id="element-454" />}
                        </button>

                        <div className="flex-1 min-w-0" data-id="element-455">
                          <p className={`font-medium text-sm transition-all ${item.checked ? 'line-through text-stone-400' : 'text-stone-800'}`} data-id="element-456">
                            {item.name}
                          </p>
                        </div>

                        <div className="flex items-center gap-3" data-id="element-457">
                          <span className={`text-sm font-semibold whitespace-nowrap ${item.checked ? 'text-stone-400' : 'text-green-600'}`} data-id="element-458">
                            {formatQuantity(item.baseQuantity, item.unit)}
                          </span>
                          <button onClick={() => removeItem(item.id)} className="text-stone-300 hover:text-red-500 transition-colors" data-id="element-459">
                            <X size={16} data-id="element-460" />
                          </button>
                        </div>
                      </motion.li>)}
                  </ul>
                </div>)}

              {/* Add Custom Item */}
              <div className="bg-white rounded-2xl shadow-md border border-stone-100 p-4" data-id="element-461">
                <label className="block text-sm font-bold text-stone-700 mb-2" data-id="element-462">
                  ➕ Add Custom Item
                </label>
                <div className="flex gap-2" data-id="element-463">
                  <input type="text" value={customItemInput} onChange={e => setCustomItemInput(e.target.value)} onKeyDown={e => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  addCustomItem();
                }
              }} placeholder="e.g. paper towels, foil, etc." className="flex-1 px-4 py-2.5 border border-stone-200 rounded-xl bg-stone-50 focus:border-green-400 focus:ring-2 focus:ring-green-100 outline-none text-sm" data-id="element-464" />
                  <button onClick={addCustomItem} disabled={!customItemInput.trim()} className="px-5 py-2.5 bg-green-600 hover:bg-green-700 disabled:bg-stone-300 text-white rounded-xl font-semibold text-sm transition-colors" data-id="element-465">
                    Add
                  </button>
                </div>
              </div>
            </motion.div>}
        </AnimatePresence>

        {/* Empty State */}
        {ingredients.length === 0 && !showSearch && <div className="text-center py-16 bg-white rounded-2xl border border-stone-100" data-id="element-466">
            <p className="text-4xl mb-3" data-id="element-467">🛒</p>
            <p className="text-xl font-bold text-stone-700 mb-2" data-id="element-468">
              Your shopping list is empty
            </p>
            <p className="text-stone-400 mb-4" data-id="element-469">
              Add recipes above to build your ingredient list.
            </p>
            <button onClick={() => setShowSearch(true)} className="bg-green-600 hover:bg-green-700 text-white px-6 py-2.5 rounded-xl font-semibold transition-colors" data-id="element-470">
              Add a Recipe
            </button>
          </div>}
      </div>
    </section>;
}